/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useContext} from 'react'
import {defineMessage, IntlContext} from 'react-intl'
import PropTypes from 'prop-types'
import {Icon, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Our own SVG imports. These will be extracted to a single sprite sheet by the
// svg-sprite-loader webpack plugin at build time and injected in the <body> tag
// during SSR.
// NOTE: Another solution would be to use `require-context.macro` package to accomplish
// importing icon svg's.
import '@salesforce/retail-react-app/app/assets/svg/alert.svg'
import '@salesforce/retail-react-app/app/assets/svg/file.svg'
import '../../assets/svg/apple.svg'
import '../../assets/svg/close-icon.svg'
import '../../assets/svg/very-sad.svg'
import '../../assets/svg/sad.svg'
import '../../assets/svg/neutral.svg'
import '../../assets/svg/happy.svg'
import '../../assets/svg/very-happy.svg'
import '../../assets/svg/star-outline.svg'
import '../../assets/svg/star-filled.svg'
import '../../assets/svg/check-mark.svg'
import '../../assets/svg/checkout-step-check.svg'
import '@salesforce/retail-react-app/app/assets/svg/check-circle.svg'
import '../../assets/svg/chevron-down.svg'
import '../../assets/svg/chevron-left.svg'
import '../../assets/svg/chevron-right.svg'
import '../../assets/svg/google.svg'
import '@salesforce/retail-react-app/app/assets/svg/hamburger.svg'
import '@salesforce/retail-react-app/app/assets/svg/info.svg'
import '../../assets/svg/social-facebook.svg'
import '@salesforce/retail-react-app/app/assets/svg/lock.svg'
import '@salesforce/retail-react-app/app/assets/svg/receipt.svg'
import '../../assets/svg/visibility.svg'
import '../../assets/svg/visibility-off.svg'
import '../../assets/svg/heart.svg'
import '../../assets/svg/heart-solid.svg'
import '../../assets/svg/thumbs-up.svg'
import '../../assets/svg/thumbs-up-dark.svg'
import '../../assets/svg/chevron-down-type2.svg'
import '../../assets/svg/custom-close.svg'
import '../../assets/svg/custom-close-white.svg'
import '../../assets/svg/general-search.svg'
import '../../assets/svg/smart-search.svg'
import '../../assets/svg/fis-back.svg'
import '../../assets/svg/starred-brand-logo.svg'
import '../../assets/svg/chevron-up-checkout.svg'
import '../../assets/svg/ugc-chevrons-up.svg'
import '../../assets/svg/edit-pencil.svg'
import '../../assets/svg/notify-me-envelope.svg'
import '../../assets/svg/fit-icon.svg'
import '../../assets/svg/apple-qr.svg'
import '../../assets/svg/android-qr.svg'
import '../../assets/svg/check-circle-reverted.svg'
import '../../assets/svg/verified-buyer.svg'
import '../../assets/svg/standard_shipping.svg'
import '../../assets/svg/express_shipping.svg'
import '../../assets/svg/uber_shipping.svg'
import '../../assets/svg/uber-shipping-black.svg'
import '../../assets/svg/pickup-in-store_shipping.svg'
import '../../assets/svg/globe.svg'
import '../../assets/svg/globe-dark.svg'
import '../../assets/svg/mini-cart.svg'
import '../../assets/svg/product-video-mute.svg'
import '../../assets/svg/product-video-pause.svg'
import '../../assets/svg/product-video-play.svg'
import '../../assets/svg/product-video-sound.svg'
import '../../assets/svg/header-menu.svg'
import '../../assets/svg/left-arrow.svg'
import '../../assets/svg/right-arrow-white.svg'
import '../../assets/svg/account-icon.svg'
import '../../assets/svg/need-help.svg'
import '../../assets/svg/loyalty-icon.svg'
import '../../assets/svg/gift-icon.svg'
import '../../assets/svg/collectif-logo-garage.svg'
import '../../assets/svg/collectif-logo-dynamite.svg'
import '../../assets/svg/order-icon.svg'
import '../../assets/svg/logout-icon.svg'
import '../../assets/svg/dollar-icon.svg'
import '../../assets/svg/rewards-icon.svg'
import '../../assets/svg/applepay-logo.svg'
import '../../assets/svg/klarna.svg'
import '../../assets/svg/google-pay.svg'
import '../../assets/svg/qr-code-icon.svg'
import '../../assets/svg/visa.svg'
import '../../assets/svg/amex.svg'
import '../../assets/svg/jcb.svg'
import '../../assets/svg/mastercard.svg'
import '../../assets/svg/unionpay.svg'
import '../../assets/svg/rewards-logo-desktop.svg'
import '../../assets/svg/rewards-logo-mobile.svg'
import '../../assets/svg/rewards-transaction.svg'
import '../../assets/svg/complimentary-gift.svg'
import '../../assets/svg/info-circle.svg'
import '../../assets/svg/info-circle-black.svg'
import '../../assets/svg/sms-optin-icon.svg'
import '../../assets/svg/geolocation.svg'
import '../../assets/svg/play.svg'
import '../../assets/svg/pause.svg'
import '../../assets/svg/mute.svg'
import '../../assets/svg/unmute.svg'
import '../../assets/svg/calendar.svg'
import '../../assets/svg/calendar-grey.svg'
import '../../assets/svg/error-icon.svg'
import '../../assets/svg/confirmation-icon.svg'
import '../../assets/svg/notify-me-return-icon.svg'
import '../../assets/svg/notify-me-submit-icon.svg'
import '../../assets/svg/download-app-store.svg'
import '../../assets/svg/download-google-play.svg'
import '../../assets/svg/plp-grid-default.svg'
import '../../assets/svg/plp-grid-default-selected.svg'
import '../../assets/svg/plp-grid-alternative.svg'
import '../../assets/svg/plp-grid-alternative-selected.svg'
import '../../assets/svg/alert-triangle-icon.svg'
import '../../assets/svg/secure-lock.svg'
import '../../assets/svg/quantity-minus.svg'
import '../../assets/svg/quantity-plus.svg'
import '../../assets/svg/toast-check-circle.svg'
import '../../assets/svg/chat.svg'
import '../../assets/svg/dot-icon.svg'
import '../../assets/svg/barcode-icon.svg'
import '../../assets/svg/return-icon.svg'
import '../../assets/svg/amex-payment-icon.svg'
import '../../assets/svg/wallet-apple.svg'
import '../../assets/svg/wallet-google.svg'
import '../../assets/svg/wallet-apple-fr.svg'
import '../../assets/svg/wallet-google-fr.svg'
import '../../assets/svg/copy-icon.svg'
import '../../assets/svg/share-icon.svg'
import '../../assets/svg/info-circle-filled.svg'
import '../../assets/svg/blue-badge.svg'
import '../../assets/svg/red-badge.svg'
import '../../assets/svg/yellow-badge.svg'
import '../../assets/svg/green-badge.svg'
import '../../assets/svg/shipping_icon.svg'
import '../../assets/svg/pickup-in-store_shipping.svg'
import '../../assets/svg/garage-brand.svg'
import '../../assets/svg/x-circle.svg'
import '../../assets/svg/return-order.svg'
import '../../assets/svg/track-order.svg'
import '../../assets/svg/alert-icon.svg'

// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
import AmexSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-amex.svg'
import BrandLogoSymbol from '../../assets/svg/brand-logo.svg'
import CVVSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-cvv.svg'
import DiscoverSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-discover.svg'
import DynBrandLogoSymbol from '../../assets/svg/dyn-brand-logo.svg'
import LocationSymbol from '@salesforce/retail-react-app/app/assets/svg/location.svg'
import MastercardSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-mastercard.svg'
import PaypalSymbol from '@salesforce/retail-react-app/app/assets/svg/paypal.svg'
import SocialPinterestSymbol from '@salesforce/retail-react-app/app/assets/svg/social-pinterest.svg'
import VisaSymbol from '@salesforce/retail-react-app/app/assets/svg/cc-visa.svg'
import ComplimentaryGiftSymbol from '../../assets/svg/complimentary-gift.svg'

// TODO: We're hardcoding the `viewBox` for these imported SVGs temporarily as the
// SVG loader plugin is not properly providing us the symbol data on the client side.
AmexSymbol.viewBox = '0 0 38 22'
BrandLogoSymbol.viewBox = '0 0 179 48'
CVVSymbol.viewBox = '0 0 41 24'
DiscoverSymbol.viewBox = '0 0 38 22'
LocationSymbol.viewBox = '0 0 16 21'
MastercardSymbol.viewBox = '0 0 38 22'
PaypalSymbol.viewBox = '0 0 80 20'
SocialPinterestSymbol.viewBox = '0 0 21 20'
VisaSymbol.viewBox = '0 0 38 22'
DynBrandLogoSymbol.viewBox = '0 0 179 48'
ComplimentaryGiftSymbol.viewBox = '0 0 33 33'

/**
 * A helper for creating a Chakra-wrapped icon from our own SVG imports via sprite sheet.
 * @param {string} name - the filename of the imported svg (does not include extension)
 * @param {Object} passProps - props that will be passed onto the underlying Icon component
 * @param {Object} localizationAttributes - attributes with localized values that will be passed
 *      onto the underlying Icon component, use `defineMessage` to create localized string.
 *      Additionally, if the icon is rendered outside the provider tree, you'll also need to
 *      pass an intl object from react-intl as a prop to translate the messages.
 */
/* istanbul ignore next */
export const icon = (name, passProps, localizationAttributes) => {
    const displayName = name
        .toLowerCase()
        .replace(/(?:^|[\s-/])\w/g, (match) => match.toUpperCase())
        .replace(/-/g, '')
    const component = forwardRef((props, ref) => {
        const theme = useTheme()
        // NOTE: We want to avoid `useIntl` here because that throws when <IntlProvider> is not in
        // the component ancestry, but we only enforce `intl` if we have `localizationAttributes`.
        let intl = useContext(IntlContext)
        if (localizationAttributes) {
            if (props?.intl) {
                const {intl: intlProp, ...otherProps} = props
                // Allow `props.intl` to take precedence over the intl we found
                intl = intlProp
                props = otherProps
            }
            if (!intl) {
                throw new Error(
                    'To localize messages, you must either have <IntlProvider> in the component ancestry or provide `intl` as a prop'
                )
            }
            Object.keys(localizationAttributes).forEach((key) => {
                passProps[key] = intl.formatMessage(localizationAttributes[key])
            })
        }
        const baseStyle = theme?.components?.Icon?.baseStyle
        return (
            <Icon ref={ref} role="presentation" {...baseStyle} {...props} {...passProps}>
                <use xlinkHref={`#${name}`} />
            </Icon>
        )
    })

    component.propTypes = {
        intl: PropTypes.object
    }

    component.displayName = `${displayName}Icon`
    return component
}

// Export Chakra icon components that use our SVG sprite symbol internally
// For non-square SVGs, we can use the symbol data from the import to set the
// proper viewBox attribute on the Icon wrapper.
export const AlertIcon = icon('alert')
export const FileIcon = icon('file')
export const AppleIcon = icon('apple')
export const BrandLogo = icon('brand-logo', {viewBox: BrandLogoSymbol.viewBox})
export const DynBrandLogo = icon('dyn-brand-logo', {viewBox: DynBrandLogoSymbol.viewBox})
export const CheckMarkIcon = icon('check-mark')
export const CheckoutStepCheckIcon = icon('checkout-step-check', {viewBox: '0 0 12 12'})
export const CheckCircleIcon = icon('check-circle')
export const ChevronDownIcon = icon('chevron-down')
export const ChevronLeftIcon = icon('chevron-left')
export const ChevronRightIcon = icon('chevron-right')
export const ChevronUpIcon = icon('chevron-up-checkout')
export const UgcChevronsUpIcon = icon('ugc-chevrons-up')
export const CustomClose = icon('custom-close')
export const CustomCloseWhite = icon('custom-close-white')
export const GeneralSearchIcon = icon('general-search')
export const SmartSearchIcon = icon('smart-search')
export const MiniCartIcon = icon('mini-cart')
export const HeaderMenuIcon = icon('header-menu')
export const ProductVideoPlayIcon = icon('product-video-play')
export const ProductVideoPauseIcon = icon('product-video-pause')
export const ProductVideoMuteIcon = icon('product-video-mute')
export const ProductVideoSoundIcon = icon('product-video-sound')
export const GoogleIcon = icon('google')
export const HeartIcon = icon('heart')
export const HeartSolidIcon = icon('heart-solid')
export const LockIcon = icon(
    'lock',
    {
        'aria-hidden': false,
        focusable: true
    },
    {
        'aria-label': defineMessage({
            id: 'icons.assistive_msg.lock',
            defaultMessage: 'Secure'
        })
    }
)
export const LocationIcon = icon('location')
export const PaypalIcon = icon('paypal', {viewBox: PaypalSymbol.viewBox})
export const PlugIcon = icon('plug')
export const PlusIcon = icon('plus')
export const MastercardIcon = icon('cc-mastercard', {viewBox: MastercardSymbol.viewBox})
export const ReceiptIcon = icon('receipt')
export const SocialFacebookIcon = icon('social-facebook')
export const VisibilityIcon = icon('visibility')
export const VisibilityOffIcon = icon('visibility-off')
export const ThumbsUp = icon('thumbs-up')
export const ThumbsUpDark = icon('thumbs-up-dark')
export const ChevronDownType2 = icon('chevron-down-type2')
export const StarredBrandLogo = icon('starred-brand-logo', {viewBox: '0 0 43 8'})
export const EditPencil = icon('edit-pencil')
export const NotifyMeEnvelope = icon('notify-me-envelope')
export const FitIcon = icon('fit-icon')
export const AddIcon = icon('quantity-plus', {viewBox: '0 0 20 20'})
export const AppleQR = icon('apple-qr')
export const AndroidQR = icon('android-qr')
export const CheckCircleReverted = icon('check-circle-reverted')
export const VerifiedBuyerIcon = icon('verified-buyer')
export const StandardShipping = icon('standard_shipping')
export const ExpressShipping = icon('express_shipping', {viewBox: '0 0 36 24'})
export const UberShipping = icon('uber_shipping', {viewBox: '0 0 39 24'})
export const UberShippingBlack = icon('uber-shipping-black')
export const InStoreShipping = icon('pickup-in-store_shipping')
export const Globe = icon('globe')
export const GlobeDark = icon('globe-dark')
export const LeftArrow = icon('left-arrow')
export const RightArrowWhite = icon('right-arrow-white')
export const Account = icon('account-icon')
export const NeedHelpIcon = icon('need-help')
export const LoyaltyIcon = icon('loyalty-icon')
export const GiftIcon = icon('gift-icon')
export const CollectifLogoGarage = icon('collectif-logo-garage', {viewBox: '0 0 211 16'})
export const CollectifLogoDynamite = icon('collectif-logo-dynamite', {viewBox: '0 0 251 16'})
export const OrderIcon = icon('order-icon')
export const LogoutIcon = icon('logout-icon')
export const DollarIcon = icon('dollar-icon')
export const RewardsIcon = icon('rewards-icon')
export const ApplepayIcon = icon('applepay-logo', {viewBox: '0 0 41 26'})
export const KlarnaIcon = icon('klarna', {viewBox: '0 0 70 30'})
export const GooglePayIcon = icon('google-pay', {viewBox: '0 0 46 24'})
export const QRCodeIcon = icon('qr-code-icon')
export const Visa = icon('visa')
export const Amex = icon('amex')
export const Jcb = icon('jcb')
export const Mastercard = icon('mastercard')
export const UnionPay = icon('unionpay')
export const RewardsLogoDesktop = icon('rewards-logo-desktop', {viewBox: '0 0 240 69'})
export const RewardsLogoMobile = icon('rewards-logo-mobile', {viewBox: '0 0 170 49'})
export const RewardsTransaction = icon('rewards-transaction')
export const ComplimentaryGiftIcon = icon('complimentary-gift', {viewBox: ComplimentaryGiftSymbol.viewBox || '0 0 33 33'})
export const InfoCircle = icon('info-circle')
export const InfoCircleBlack = icon('info-circle-black', {viewBox: '0 0 17 17'})
export const SMSOptInIcon = icon('sms-optin-icon')
export const Geolocation = icon('geolocation')
export const Play = icon('play')
export const Pause = icon('pause')
export const Mute = icon('mute')
export const Unmute = icon('unmute')
export const CalendarIcon = icon('calendar')
export const CalendarGreyIcon = icon('calendar-grey')
export const ErrorIcon = icon('error-icon')
export const ConfirmationIcon = icon('confirmation-icon', {viewBox: '0 0 20 20'})
export const NotifyMeReturnIcon = icon('notify-me-return-icon', {viewBox: '0 0 20 20'})
export const NotifyMeSubmitIcon = icon('notify-me-submit-icon', {viewBox: '0 0 24 24'})
export const DownloadAppIcon = icon('download-app-store', {viewBox: '0 0 120 40'})
export const DownloadPlayIcon = icon('download-google-play', {viewBox: '0 0 135 40'})
export const PlpGridDefaultIcon = icon('plp-grid-default')
export const PlpGridDefaultSelectedIcon = icon('plp-grid-default-selected')
export const PlpGridAlternativeIcon = icon('plp-grid-alternative')
export const PlpGridAlternativeSelectedIcon = icon('plp-grid-alternative-selected')
export const AlertTriangleIcon = icon('alert-triangle-icon')
export const SecureLock = icon('secure-lock', {viewBox: '0 0 16 16'})
export const MinusIcon = icon('quantity-minus', {viewBox: '0 0 20 20'})
export const CheckCircleWhite = icon('toast-check-circle', {viewBox: '0 0 20 20'})
export const ChatIcon = icon('chat', {viewBox: '0 0 24 24'})
export const DotIcon = icon('dot-icon', {viewBox: '0 0 20 20'})
export const BarcodeIcon = icon('barcode-icon', {viewBox: '0 0 24 24'})
export const ReturnIcon = icon('return-icon', {viewBox: '0 0 20 20'})
export const AmexPaymentIcon = icon('amex-payment-icon', {viewBox: '0 0 38 22'})
export const WalletAppleIcon = icon('wallet-apple', {viewBox: '0 0 155 48'})
export const WalletGoogleIcon = icon('wallet-google', {viewBox: '0 0 174 48'})
export const WalletAppleFrIcon = icon('wallet-apple-fr', {viewBox: '0 0 113.408 35.291'})
export const WalletGoogleFrIcon = icon('wallet-google-fr', {viewBox: '0 0 199 55'})
export const CopyIcon = icon('copy-icon', {viewBox: '0 0 24 24'})
export const LinkIcon = forwardRef(function LinkIcon({w, h, width, height, flexShrink, color, ...props}, ref) {
    return (
        <svg
            ref={ref}
            xmlns="http://www.w3.org/2000/svg"
            width={w ?? width ?? '21'}
            height={h ?? height ?? '11'}
            viewBox="0 0 21 11"
            fill="none"
            style={{flexShrink}}
            {...props}
        >
            <path
                d="M12.521 0.5H15.5C18.261 0.5 20.5 2.738 20.5 5.5C20.5 8.261 18.261 10.5 15.5 10.5H12.5M8.521 10.5H5.5C2.738 10.5 0.5 8.261 0.5 5.5C0.5 2.738 2.738 0.5 5.5 0.5H8.49M5.5 5.5H15.5"
                stroke="currentColor"
                strokeLinecap="square"
            />
        </svg>
    )
})
export const ShareIcon = icon('share-icon', {viewBox: '0 0 24 24'})
export const InfoCircleFilled = icon('info-circle-filled')
export const BlueBadge = icon('blue-badge', {viewBox: '0 0 12 12'})
export const GreenBadge = icon('green-badge', {viewBox: '0 0 12 12'})
export const YellowBadge = icon('yellow-badge', {viewBox: '0 0 12 12'})
export const RedBadge = icon('red-badge', {viewBox: '0 0 12 12'})
export const ShippingIcon = icon('shipping_icon')
export const GarageBrandIcon = icon('garage-brand', {viewBox: '0 0 43 8'})
export const CrossCircle = icon('x-circle')
export const TrackOrder = icon('track-order')
export const ReturnOrder = icon('return-order')
export const AlertIconCustom = icon('alert-icon')
export const CsatCloseIcon = icon('close-icon', {viewBox: '0 0 17 18'})
export const VerySadIcon = icon('very-sad', {viewBox: '0 0 23 23'})
export const SadIcon = icon('sad', {viewBox: '0 0 23 23'})
export const NeutralIcon = icon('neutral', {viewBox: '0 0 23 23'})
export const HappyIcon = icon('happy', {viewBox: '0 0 23 23'})
export const VeryHappyIcon = icon('very-happy', {viewBox: '0 0 23 23'})
export const StarOutlineIcon = icon('star-outline', {viewBox: '0 0 26 25'})
export const StarFilledIcon = icon('star-filled', {viewBox: '0 0 26 25'})
